import React from 'react';
import { Select } from 'antd';
import { Toggle } from 'src/components/shared/toggle/toggle';
import { TooltipDisableReason } from 'src/components/shared/tooltip-disable-message/tooltip-disable-reason';
import Arrow from '@svg/chevron-down_20.svg?react';
import { ProtectionPlusOptionInfo } from './protection-plus';
import { getEnumOptions, ProtectionPlusOptions, FeatureName, BulkProtectionOptions, ProtectionPlusTitles } from 'src/utils/enums';
import { DNS_DISABLE_ANTISPYWARE_HAS_INACTIVE } from 'src/utils/constants';
import { useActivation } from 'src/utils/activationUtil';
import { ThreatFeatureStructure } from './protection-plus-config';

interface ThreatFeatureProps {
    feature: ThreatFeatureStructure;
    features: Record<string, string>;
    hasPermissions: boolean;
    disableReason: string;
    onChange: (selectedValue: string | boolean, threatItem: ThreatFeatureStructure) => void;
}

export const ThreatFeature: React.FC<ThreatFeatureProps> = ({
    feature,
    features,
    hasPermissions,
    disableReason,
    onChange
}) => {

    const { isPageDisabled } = useActivation();

    const getDnsDisableReason = (nameForUpdate: FeatureName | undefined) => {
        if (
            nameForUpdate === FeatureName.DNS &&
            (features[FeatureName.SPYWARE_PROTECTION] === 'Inactive' ||
                (features[FeatureName.SPYWARE_PROTECTION] === 'Manual' && features['hasAnyAntispywareProfileInactive']))
        ) {
            return DNS_DISABLE_ANTISPYWARE_HAS_INACTIVE;
        }
        return '';
    };

    const getValue = (threatItem: ThreatFeatureStructure) => {
        if (threatItem.key === ProtectionPlusTitles.DYNAMIC_DEFENSE) {
            return !isPageDisabled[BulkProtectionOptions.PROTECTION_PLUS];
        }
        return features[threatItem.nameForUpdate ?? ''];
    };

    const value = getValue(feature);

    const input = feature.type === 'toggle'
        ? <Toggle
            label="ACTIVE"
            inactiveLabel="INACTIVE"
            value={value as boolean}
            onChange={(val) => onChange(val, feature)}
            disableReason={disableReason || getDnsDisableReason(feature.nameForUpdate)}
        />
        : <Select
            key={feature.key}
            className={`protection-option-select ${features[feature.nameForUpdate || '']?.toLowerCase()}`}
            disabled={!!disableReason || !hasPermissions || !!getDnsDisableReason(feature.nameForUpdate)}
            popupClassName="dropdown-custom"
            value={value}
            placeholder={!hasPermissions ? 'Select' : ''}
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            options={getEnumOptions(ProtectionPlusOptions)}
            onSelect={(val) => onChange(val, feature)}
            suffixIcon={<Arrow />}
        />;

    const className = feature.key?.toLowerCase().replace(/\s+/g, '-');
    return (
        <div className={`protection-plus-option ${className}`} key={feature.key} data-testid={feature.key}>
            <ProtectionPlusOptionInfo
                icon={feature.icon}
                title={feature.key ?? ''}
                explain={feature.explain}
                additionalExplain={feature.additionalExplain?.(value as string)}
            />
            <TooltipDisableReason
                disableReason={disableReason || getDnsDisableReason(feature.nameForUpdate)}
                missingPermissions={!hasPermissions}
            >
                {input}
            </TooltipDisableReason>
        </div>
    );
};
